import React, { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBullhorn, faTag, faReceipt } from "@fortawesome/free-solid-svg-icons";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE } from "../../config/api";
import useChatSocket from "../../context/ChatSocket/useChatSocket";
import { playNewOrderChime, playNewMessageChime } from "../../utils/soundAlerts";
import style from "./style.module.css";

interface FeedItem {
  source: "order" | "broadcast";
  order_notification_id?: number;
  notification_id?: number;
  order_id?: number;
  type?: string;
  // Only present for source: "order" — a raw OrderNotifications row. Either
  // a lifecycle value ('placed', 'confirming', ...) or 'chat_<message_id>'
  // for a chat-message ping (see docs/order-chat-plan.md) — used to decide
  // where clicking the item should navigate.
  status_name?: string;
  is_read?: boolean;
  title: string;
  message: string;
  created_at: string;
}

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const iconForItem = (item: FeedItem) => {
  if (item.source === "broadcast") {
    return item.type === "announcement" ? faBullhorn : faTag;
  }
  return faReceipt;
};

// Surfaces a staff member's own notification feed (most notably "a customer
// placed an order") right in the sidebar via a dropdown panel, so Owner/
// Admin/Sales don't have to leave whatever page they're on. Uses the same
// /notification/mine and /notification/unread-count endpoints the mobile
// app uses — canReadOwn already scopes both by req.user.user_id regardless
// of role. This is deliberately a dropdown, not a navigate-to-page action:
// the sidebar's own "Notifications" nav entry already owns /notifications
// for Owner/Admin promotion & announcement CRUD, a different screen from a
// staff member's personal feed.
const NotificationBell: FC = () => {
  const [cookies] = useCookies();
  const navigate = useNavigate();
  const { socket } = useChatSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/notification/unread-count`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data?.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching unread notification count:", error);
    }
  };

  const fetchFeed = async () => {
    setIsLoading(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/notification/mine?page=1&limit=20`, { method: "GET" }, cookies.auth);
      if (response.ok) {
        const data = await response.json();
        setItems(data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching notification feed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  // Ask for browser-notification permission once — POS is a tab staff leave
  // open all day, so a real OS-level toast for a new order/chat ping is far
  // more likely to actually be seen than the in-tab badge alone. Requires a
  // secure context (HTTPS or localhost); silently unavailable otherwise.
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Live nudge: any new OrderNotifications row for this user (chat message
  // or otherwise, see chatSocket.js) pushes this event — refresh instantly
  // instead of only on mount, and surface a real browser notification (not
  // just the in-tab badge) since staff often have this tab backgrounded.
  useEffect(() => {
    if (!socket) return;
    const handleNewNotification = (payload?: { title?: string; message?: string; status_name?: string }) => {
      fetchUnreadCount();
      if (payload?.status_name?.startsWith("chat_")) {
        playNewMessageChime();
      } else {
        playNewOrderChime();
      }
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification(payload?.title || "New notification", { body: payload?.message || "" });
      }
    };
    socket.on("notification:new", handleNewNotification);
    return () => { socket.off("notification:new", handleNewNotification); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedContainer = containerRef.current && containerRef.current.contains(target);
      const clickedPanel = panelRef.current && panelRef.current.contains(target);
      if (!clickedContainer && !clickedPanel) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The panel is portaled to document.body (not rendered inline) because the
  // sidebar has `overflow-x: hidden`/`overflow-y: auto` — an absolutely
  // positioned child would just get clipped. Position is computed from the
  // bell button's own viewport rect and kept in `position: fixed`, so it
  // tracks correctly regardless of where in the DOM tree it renders.
  const toggleOpen = () => {
    const next = !isOpen;
    if (next && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPanelPosition({ top: rect.bottom + 8, left: rect.left });
    }
    setIsOpen(next);
    if (next) fetchFeed();
  };

  // Marking read and navigating are separate concerns — previously bundled
  // into one early-returning function, which meant clicking an
  // already-read item (e.g. a second click, or one that arrived read from
  // the server) did nothing at all, not even navigate. Every source:"order"
  // item now navigates regardless of read state; only the read-marking
  // itself is conditional.
  const handleItemClick = async (item: FeedItem) => {
    if (item.source !== "order" || !item.order_id) return;

    if (!item.is_read) {
      try {
        const response = await authenticatedFetch(
          `${API_BASE}/notification/order-notification/${item.order_notification_id}/read`,
          { method: "PUT" },
          cookies.auth
        );
        if (response.ok) {
          setItems((prev) => prev.map((i) =>
            i.source === "order" && i.order_notification_id === item.order_notification_id
              ? { ...i, is_read: true }
              : i
          ));
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
      } catch (error) {
        console.error("Error marking notification read:", error);
      }
    }

    setIsOpen(false);
    // A chat-message ping (status_name: 'chat_<message_id>', see
    // docs/order-chat-plan.md) opens that order's chat panel directly;
    // any other order-lifecycle notification filters OrderPage's table
    // down to that order via its existing order-ID search instead — see
    // OrderPage.tsx's ?chat=/?search= handling.
    if (item.status_name?.startsWith("chat_")) {
      navigate(`/order?chat=${item.order_id}`);
    } else {
      navigate(`/order?search=${item.order_id}`);
    }
  };

  return (
    <div className={style.container} ref={containerRef}>
      <button
        ref={buttonRef}
        className={style.bellButton}
        onClick={toggleOpen}
        title="Notifications"
      >
        <FontAwesomeIcon icon={faBell} />
        {unreadCount > 0 && (
          <span className={style.badge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {isOpen && createPortal(
        <div
          ref={panelRef}
          className={style.panel}
          style={{ top: panelPosition.top, left: panelPosition.left }}
        >
          <div className={style.panelHeader}>Notifications</div>
          <div className={style.panelBody}>
            {isLoading ? (
              <div className={style.panelState}>Loading...</div>
            ) : items.length === 0 ? (
              <div className={style.panelState}>Nothing here yet.</div>
            ) : (
              items.map((item) => (
                <div
                  key={`${item.source}-${item.order_notification_id ?? item.notification_id}`}
                  className={`${style.item} ${item.source === "order" && !item.is_read ? style.itemUnread : ""}`}
                  onClick={() => handleItemClick(item)}
                >
                  <FontAwesomeIcon icon={iconForItem(item)} className={style.itemIcon} />
                  <div className={style.itemBody}>
                    <div className={style.itemTitle}>{item.title}</div>
                    <div className={style.itemMessage}>{item.message}</div>
                    <div className={style.itemDate}>{formatDate(item.created_at)}</div>
                  </div>
                  {item.source === "order" && !item.is_read && <span className={style.itemDot} />}
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
